// @givmo/org-console — the organization QR design's pure rules (FCM-WEB4; plan §FC-14).
//
// The editor edits a WORKING `QrDesignSpec` — the one wire shape the server validates and renders, and
// the one the Givmo apps edit (Champion QR's design system). This module holds the parts of that
// editing which are pure: the Givmo standard design a reset returns to, the apps' constrained
// NON-colour setters (iOS `QRDesignEditorViewModel`), the readouts, and whether two designs are the
// same design.
//
// ⚠ COLOURS ARE NEVER CORRECTED HERE, BY LAW (§FC-14 "Colors differ by law"). Both apps darken a
// too-light colour on every change; the web keeps the colour it was given and lets the SERVER judge
// it — a design the server refuses keeps the last good preview and says why. There is deliberately no
// fourth copy of the scannability rule in this repo.
//
// ⚠ EACH SETTER EDITS ONLY ITS OWN FIELDS; everything else round-trips untouched, so a design saved in
// an app (a field this editor has no control for) survives a web save byte-for-byte.

import type { QrDesignSpec } from '@givmo/api-client';

export type QrModuleShape = 'square' | 'rounded' | 'circle';
export type QrLogoKind = 'givmo' | 'entity';

/**
 * The Givmo standard design — the server's `default_givmo_preset()` (backend
 * `app/domain/entities/qr_design_entities.py`), field for field: what an organization that never
 * saved a design renders in, and what "Reset to Givmo standard" returns the working design to.
 */
/** The standard design's logo size. */
export const QR_STANDARD_LOGO_SCALE = 0.2;

export const QR_DESIGN_STANDARD: QrDesignSpec = {
  schema_version: 1,
  fg_color: '#000000',
  bg_color: '#ffffff',
  gradient: null,
  module_shape: 'square',
  eye: { auto: true },
  logo: { kind: 'givmo', ref: null, scale: QR_STANDARD_LOGO_SCALE },
  frame: { enabled: false, style: 'none', caption: null },
  ecc: 'H',
  quiet_zone: 4,
  geometry: {
    dot_diameter_frac: 1,
    corner_radius_frac: 0,
    gap_frac: 0,
    logo_knockout_frac: 0.25,
  },
};

/** The logo's side as a fraction of the code's side — the apps' slider bounds. */
export const QR_MIN_LOGO_SCALE = 0.05;
export const QR_MAX_LOGO_SCALE = 0.4;
/** The frame caption's server limit; the only text a render carries. */
export const QR_MAX_CAPTION_LENGTH = 60;
/** The caption field's placeholder — the apps' own. */
export const QR_CAPTION_PLACEHOLDER = 'Scan to give';

export const withModuleShape = (spec: QrDesignSpec, module_shape: QrModuleShape): QrDesignSpec => ({
  ...spec,
  module_shape,
});

export const withForeground = (spec: QrDesignSpec, fg_color: string): QrDesignSpec => ({
  ...spec,
  fg_color,
});

export const withBackground = (spec: QrDesignSpec, bg_color: string): QrDesignSpec => ({
  ...spec,
  bg_color,
});

/** On: a linear gradient seeded from the dots colour at 0°, as the apps seed it. Off: none. */
export function withGradient(spec: QrDesignSpec, enabled: boolean): QrDesignSpec {
  if (!enabled) return { ...spec, gradient: null };
  if (spec.gradient) return spec;
  return {
    ...spec,
    gradient: { type: 'linear', from_color: spec.fg_color, to_color: spec.fg_color, angle: 0 },
  };
}

export function withGradientStop(
  spec: QrDesignSpec,
  stop: 'from_color' | 'to_color',
  color: string,
): QrDesignSpec {
  return spec.gradient ? { ...spec, gradient: { ...spec.gradient, [stop]: color } } : spec;
}

export function withGradientAngle(spec: QrDesignSpec, angle: number): QrDesignSpec {
  return spec.gradient ? { ...spec, gradient: { ...spec.gradient, angle } } : spec;
}

/** The logo choice clears any `ref`, exactly as the apps' setter does. */
export function withLogoKind(spec: QrDesignSpec, kind: QrLogoKind): QrDesignSpec {
  const logo = spec.logo ?? QR_DESIGN_STANDARD.logo!;
  return { ...spec, logo: { ...logo, kind, ref: null } };
}

/** Clamped to the apps' bounds, so the editor never sends a scale the server refuses. */
export function withLogoScale(spec: QrDesignSpec, scale: number): QrDesignSpec {
  const logo = spec.logo ?? QR_DESIGN_STANDARD.logo!;
  return {
    ...spec,
    logo: { ...logo, scale: Math.min(Math.max(scale, QR_MIN_LOGO_SCALE), QR_MAX_LOGO_SCALE) },
  };
}

/** A frame on draws the rounded frame; off clears the caption with it (the apps' setter). */
export function withFrame(spec: QrDesignSpec, enabled: boolean): QrDesignSpec {
  const frame = spec.frame ?? QR_DESIGN_STANDARD.frame!;
  return {
    ...spec,
    frame: {
      ...frame,
      enabled,
      style: enabled ? 'rounded' : 'none',
      caption: enabled ? (frame.caption ?? null) : null,
    },
  };
}

/** Kept as typed (up to the limit); a caption of only spaces is no caption. */
export function withCaption(spec: QrDesignSpec, caption: string): QrDesignSpec {
  const frame = spec.frame ?? QR_DESIGN_STANDARD.frame!;
  const capped = caption.slice(0, QR_MAX_CAPTION_LENGTH);
  return { ...spec, frame: { ...frame, caption: capped.trim().length > 0 ? capped : null } };
}

/** Every optional field filled with the server's default, hex lower-cased, keys sorted. */
function canonical(spec: QrDesignSpec): string {
  const standard = QR_DESIGN_STANDARD;
  const filled = {
    schema_version: spec.schema_version ?? standard.schema_version,
    fg_color: spec.fg_color.toLowerCase(),
    bg_color: spec.bg_color.toLowerCase(),
    gradient: spec.gradient
      ? {
          type: spec.gradient.type ?? 'none',
          from_color: spec.gradient.from_color.toLowerCase(),
          to_color: spec.gradient.to_color.toLowerCase(),
          angle: spec.gradient.angle ?? 0,
        }
      : null,
    module_shape: spec.module_shape ?? standard.module_shape,
    eye: { auto: spec.eye?.auto ?? true },
    logo: {
      kind: spec.logo?.kind ?? 'givmo',
      ref: spec.logo?.ref ?? null,
      scale: spec.logo?.scale ?? QR_STANDARD_LOGO_SCALE,
    },
    frame: {
      enabled: spec.frame?.enabled ?? false,
      style: spec.frame?.style ?? 'none',
      caption: spec.frame?.caption ?? null,
    },
    ecc: spec.ecc ?? standard.ecc,
    quiet_zone: spec.quiet_zone ?? standard.quiet_zone,
    geometry: { ...standard.geometry, ...spec.geometry },
  };
  return JSON.stringify(filled, (_key, value: unknown) =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(
          Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)),
        )
      : value,
  );
}

/** Whether two specs are the same design (defaults filled, hex compared case-insensitively). */
export function sameQrDesign(a: QrDesignSpec, b: QrDesignSpec): boolean {
  return canonical(a) === canonical(b);
}

/** A stable key for one design — the preview cache and a render's identity. */
export function qrDesignKey(spec: QrDesignSpec): string {
  return canonical(spec);
}

/** The logo size readout: the share of the largest allowed size, as the apps show it. */
export function qrLogoScaleReadout(scale: number | undefined): string {
  const value = scale ?? QR_STANDARD_LOGO_SCALE;
  return `${Math.round((value / QR_MAX_LOGO_SCALE) * 100)}%`;
}

/** The gradient angle readout, in whole degrees. */
export function qrAngleReadout(angle: number | undefined): string {
  return `${Math.round(angle ?? 0)}°`;
}

/** A `#RRGGBB` a person typed, normalized; `null` when it is not one. */
export function parseHexColor(raw: string): string | null {
  const trimmed = raw.trim();
  const hex = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex.toLowerCase() : null;
}

/** The organization's default design as the editor reads it off `GET /org/{org_ref}/qr-design`. */
export interface OrgQrDesignRead {
  spec: QrDesignSpec;
  /** A usable saved default governs the organization's QR codes. */
  saved: boolean;
  /** The organization has a stored logo a design may choose. */
  orgLogoAvailable: boolean;
  /** A saved default exists but no longer passes the server's checks: the standard renders instead. */
  savedDesignInvalid: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * The read, tolerantly: a body without a drawable spec is an error, never a silent standard design
 * (that would claim the organization saved nothing); the three flags default to their safe reading.
 */
export function decodeOrgQrDesign(body: unknown): OrgQrDesignRead {
  const record = isRecord(body) && isRecord(body.data) ? body.data : body;
  if (
    !isRecord(record) ||
    !isRecord(record.spec) ||
    typeof record.spec.fg_color !== 'string' ||
    typeof record.spec.bg_color !== 'string'
  ) {
    throw new Error('The QR code design could not be read.');
  }
  return {
    spec: record.spec as unknown as QrDesignSpec,
    saved: record.saved === true,
    orgLogoAvailable: record.org_logo_available === true,
    savedDesignInvalid: record.saved_design_invalid === true,
  };
}

/** Which of the three designs the working one is, for the editor's state line. */
export type QrDesignState = 'saved' | 'standard' | 'unsaved';

export function qrDesignState(read: OrgQrDesignRead, working: QrDesignSpec): QrDesignState {
  if (read.saved && sameQrDesign(working, read.spec)) return 'saved';
  if (!read.saved && sameQrDesign(working, QR_DESIGN_STANDARD)) return 'standard';
  return 'unsaved';
}
